import React from "react";
import { Day, Habit } from "../types";
import { DaySequenceBinary } from "../../../components/DaySequenceBinary/DaySequenceBinary";
import { asDay } from "../../../utils/dateTypes";

interface HabitBarChartProps {
  habit: Habit;
  days: Day[];
  periodDays: string[];
  height?: number;
}

export const HabitBarChart: React.FC<HabitBarChartProps> = ({
  habit,
  days,
  periodDays,
  height = 60,
}) => {
  const habitData = periodDays.map((day) => {
    const dayData = days.find((d) => d.date === day);
    const hasCompleted = dayData?.habits.includes(habit.id);
    const progress = hasCompleted ? dayData?.progress?.[habit.id] || 0 : 0;
    return { day, hasCompleted, progress };
  });

  const maxProgress = Math.max(...habitData.map((d) => d.progress), 1);

  // For progress tracking habits, use bar chart
  if (habit.progressTracking) {
    return (
      <div
        style={{
          display: "flex",
          height: `${height}px`,
          alignItems: "end",
          gap: "1px",
          paddingBottom: "4px",
          overflow: "hidden",
        }}
      >
        {habitData.map((data, index) => {
          const barHeight = Math.max(
            (data.progress / maxProgress) * (height - 10),
            data.hasCompleted ? 4 : 0,
          );

          return (
            <div
              key={index}
              style={{
                flex: 1,
                height: `${barHeight}px`,
                backgroundColor: data.hasCompleted
                  ? habit.score >= 0
                    ? "#28a745"
                    : "#dc3545"
                  : "#e9ecef",
                borderRadius: "1px 1px 0 0",
                minHeight: "2px",
                minWidth: "1px",
              }}
              title={`${new Date(data.day).toLocaleDateString()}: ${data.progress}`}
            />
          );
        })}
      </div>
    );
  }

  // For non-progress habits, use DaySequenceBinary component
  const startDate = asDay(periodDays[0]);
  const endDate = asDay(periodDays[periodDays.length - 1]);

  if (!startDate || !endDate) {
    // Fallback to original implementation if date conversion fails
    return (
      <div style={{ height: `${height}px`, display: 'flex', alignItems: 'center', color: '#999' }}>
        Invalid date range
      </div>
    );
  }

  // Convert completed habit data to event dates
  const eventDates = habitData
    .filter(data => data.hasCompleted)
    .map(data => asDay(data.day))
    .filter((day): day is NonNullable<typeof day> => day !== null);

  // Determine color based on habit score
  const habitColor = habit.score >= 0 ? "#28a745" : "#dc3545";

  return (
    <DaySequenceBinary
      startDate={startDate}
      endDate={endDate}
      eventDates={eventDates}
      color={habitColor}
      dotHeight={Math.min(height - 8, 20)}
      borderRadius={4}
    />
  );
};
